import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseHrefService } from '@nibtravel/shared/util/base-href';
import {
  CORRELATION_HEADER,
  CORRELATION_ID,
  LOGGING_CONTEXT,
  LoggingContext,
  LoggingContextProps,
  USE_CORRELATION_ID,
} from '@nibtravel/shared/util/constants';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { v4 as newGuid } from 'uuid';

import { LoggerService } from '../logger.service';
import { ErrorService } from './error.service';

/**
 * The main http interceptor that aggregates the basic usage for all http requests
 * It includes error handling and logging.
 *
 * @export
 * @class NibtravelHttpInterceptor
 * @implements {HttpInterceptor}
 */
@Injectable({ providedIn: 'root' })
export class NibtravelHttpInterceptor implements HttpInterceptor {
  constructor(
    private hrefService: BaseHrefService,
    private errorService: ErrorService,
    private logger: LoggerService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (req.url.startsWith(this.hrefService.assets)) {
      return next.handle(req);
    }

    let request = req;
    const loggingContext = req.context.get(LOGGING_CONTEXT);

    let correlationId: string;
    const useCorrelation = req.context.get(USE_CORRELATION_ID);
    if (useCorrelation) {
      correlationId = req.context.get(CORRELATION_ID);
      request = req.clone({
        headers: req.headers.set(
          CORRELATION_HEADER,
          correlationId ?? newGuid()
        ),
      });
    }

    return next.handle(request).pipe(
      tap(() => {
        if (loggingContext) {
          this.logger.info({
            value: loggingContext?.[LoggingContextProps.Message],
            category:
              loggingContext?.[LoggingContextProps.Category] ?? 'Unknown',
            method: loggingContext?.[LoggingContextProps.Method],
            context: loggingContext?.[LoggingContextProps.Context],
            correlationId,
          });
        }
      }),
      catchError((error) =>
        this.handleError(error, loggingContext, correlationId)
      )
    );
  }

  handleError(
    error: HttpErrorResponse,
    loggingContext?: LoggingContext,
    correlationId?: string
  ): Observable<never> {
    const category =
      loggingContext?.[LoggingContextProps.Category] ?? 'Server Error';
    const method =
      loggingContext?.[LoggingContextProps.Method] ??
      'NibtravelHttpInterceptor.handleError';
    const errorToRaise = this.errorService.getErrorModel(error);
    errorToRaise.markAsHandled(); // Global Error Handler will not log if marked as handled
    this.logger.error({
      value: errorToRaise,
      category,
      method,
      correlationId,
      context: loggingContext?.context,
    });
    return throwError(errorToRaise);
  }
}
